import { useCallback } from 'react';
import type { NavigationProp, ParamListBase } from '@react-navigation/native';
import { useSafeNavigation, useSafeRouter } from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import type {
  MainTabName,
  MainTabsParamList,
  MenuTabStackParamList,
} from '../../AppNavigation.props';
import { useActiveRouteName } from '../../hooks/useActiveRouteName';
import { getMenuStackScreensNames, getModalScreensNames } from '../../utils';

/**
 * If we are in a modal screen and we navigate to the main tabs,
 * We need first to dismiss the modal screen, then navigate to the main tabs,
 * But since react-navigation debounces our calls, only considering the last one,
 * We also need the setTimeout to ensure it actually does the intended steps,
 * Native uses useLinking.native.tsx which doesn't have all of this code,
 * So we only need to do this on web for useLinking.tsx.
 */
export const useNavigateToMainTabs = (
  passedNavigation?: NavigationProp<ParamListBase> | null,
) => {
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const router = useSafeRouter();

  const fallbackNavigation =
    useSafeNavigation<NavigationProp<MainTabsParamList>>();
  const menuNavigation =
    useSafeNavigation<NavigationProp<MenuTabStackParamList>>();
  const navigation =
    (passedNavigation as NavigationProp<MainTabsParamList> | null) ??
    fallbackNavigation;
  const activeRouteName = useActiveRouteName();

  // ─── Expo Router ────────────────────────────────────────────────────────────

  const navigateExpoRouter = useCallback(
    async (
      screen: MainTabName,
      params: MainTabsParamList[MainTabName] = defaultScreenParams,
    ): Promise<void> => {
      if (!router) return;

      const path = resolveExpoRouterPath(screen, params);

      if (!path) return;

      if (router.canDismiss()) {
        router.dismissAll();
      }

      router.navigate(path);
    },
    [router],
  );

  // ─── React Navigation ──────────────────────────────────────────────────────

  const navigateReactNavigation = useCallback(
    async (
      screen: MainTabName,
      params: MainTabsParamList[MainTabName] = defaultScreenParams,
    ): Promise<void> => {
      if (!navigation) return;

      return new Promise((resolve) => {
        // Navigation from the Modal Stack to one of the Main Stacks must
        // first dismiss the Modal Stack and then navigate to the Main Stack.

        const isOnModalScreen = getModalScreensNames().has(activeRouteName);
        const shouldDismissTheCurrentStack =
          isOnModalScreen && navigation.getParent;

        if (shouldDismissTheCurrentStack) {
          navigation.getParent()?.goBack();

          // We need to timeout the next navigation so it isn't debounced.
          setTimeout(() => {
            // @ts-expect-error TS(2345): Argument of type '["HomeTab" | "MenuTab" | "Loyalt... Remove this comment to see the full error message
            navigation.navigate(screen, params);
            resolve();
          });

          return;
        }

        // Navigation from within the menu stack should happen on the stack.
        // Otherwise it will result in incorrect browser url behavior.

        const menuStackScreens = getMenuStackScreensNames();
        const isInMenuStack = menuStackScreens.has(activeRouteName);
        // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
        const isGoingToMenuStack = menuStackScreens.has(params?.screen);

        if (isInMenuStack && isGoingToMenuStack && menuNavigation) {
          // @ts-expect-error TS(2345): Argument of type '["Home" | "Locations" | "MenuWit... Remove this comment to see the full error message
          menuNavigation.navigate(params?.screen, params?.params);
          resolve();

          return;
        }

        // @ts-expect-error TS(2345): Argument of type '["HomeTab" | "MenuTab" | "Loyalt... Remove this comment to see the full error message
        navigation.navigate(screen, params);
        resolve();
      });
    },
    [activeRouteName, navigation, menuNavigation],
  );

  // ─────────────────────────────────────────────────────────────────────────────

  return isExpoRouterEnabled ? navigateExpoRouter : navigateReactNavigation;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Maps React Navigation tab + screen params to expo-router paths.
 */
function resolveExpoRouterPath(
  tab: MainTabName,
  params?: MainTabsParamList[MainTabName],
): string | ExpoRouterNavigation | undefined {
  const screenName = params?.screen as string | undefined;
  const screenParams = params?.params as Record<string, string> | undefined;

  switch (tab) {
    case 'HomeTab': {
      return '/';
    }

    case 'MenuTab': {
      if (screenName === 'Menu' && screenParams?.restaurantSlug) {
        return {
          pathname: '/[restaurantSlug]/menu',
          params: { restaurantSlug: screenParams.restaurantSlug },
        };
      }

      if (screenName === 'DeliveryMenu' && screenParams?.addressId) {
        return {
          pathname: '/delivery/[addressId]/menu',
          params: { addressId: screenParams.addressId },
        };
      }

      if (screenName === 'Locations') {
        return '/locations';
      }

      return '/menu';
    }

    case 'LoyaltyTab': {
      return '/sg-rewards';
    }

    case 'AccountTab': {
      return '/account/menu';
    }

    case 'GiftCardsTab': {
      return '/gift';
    }

    case 'ReorderTab': {
      return '/reorder-tab';
    }

    default: {
      return undefined;
    }
  }
}

// ────────────────────────────────────────────────────────────────────────────────

const defaultScreenParams = {
  params: undefined,
  screen: undefined,
  state: undefined,
};

// ─── Types ────────────────────────────────────────────────────────────────────

type ExpoRouterNavigation = {
  pathname: string;
  params: Record<string, string>;
};
